<template>

  <m-pay-layout>

    <template slot="links">
      <router-link v-if="hasAdminRole"
        :to="{ name: 'merchant', params: { merchantId: transaction.merchantId } }">
        <v-btn small flat color="secondary">
          Merchant
          <v-icon small right dark class="ml-1">store</v-icon>
        </v-btn>
      </router-link>

      <router-link :to="{ name: 'order', params: { orderId: transaction.orderId } }">
        <v-btn small flat color="secondary">
          Order
          <v-icon small right dark class="ml-1">create</v-icon>
        </v-btn>
      </router-link>
      <router-link :to="{ name: 'purchase', params: { purchaseId: transaction.purchaseId } }">
        <v-btn small flat color="secondary">
          Purchase
          <v-icon small right dark class="ml-1">shopping_cart</v-icon>
        </v-btn>
      </router-link>
      <!--Generic link since we redirect to the same view
      (router-link by default does not reload the page) -->
      <span v-if="transaction.refunds && transaction.refunds.length === 1">
        <a class="v-btn--small v-card--flat altColor" :href="$router.resolve({
            name: 'transaction',
            params: { transactionId: transaction.refunds[0].transactionId }}).href">
          <v-btn small flat color="secondary">
            Refund details
            <v-icon small right dark class="ml-1">playlist_add_check</v-icon>
          </v-btn>
        </a>
      </span>
      <router-link v-if="transaction.refunds && transaction.refunds.length > 1"
               :to="{ name: 'transactions', query: { f_refundedTransactionId: transaction.id } }">
        <v-btn small flat color="secondary">
          Refund list
          <v-icon small right dark class="ml-1">playlist_add_check</v-icon>
        </v-btn>
      </router-link>
      <span v-if="transaction.refundedTransactionId">
        <a :href="$router.resolve({
            name: 'transaction',
            params: { transactionId: transaction.refundedTransactionId }}).href">
          <v-btn small flat color="secondary">
            Original transaction
            <v-icon small right dark class="ml-1">playlist_add_check</v-icon>
          </v-btn>
        </a>
      </span>

      <router-link v-if="hasAdminRole"
        :to="{ name: 'customer', params: { customerId: transaction.customerId } }">
        <v-btn small flat color="secondary">
          Customer
          <v-icon small right dark class="ml-1">person</v-icon>
        </v-btn>
      </router-link>
      <router-link v-if="hasAdminRole"
        :to="{ name: 'soap-logs', query: { f_transactionId: transaction.id } }">
        <v-btn small flat color="secondary">
          Soap Logs
          <v-icon small right dark class="ml-1">sort</v-icon>
        </v-btn>
      </router-link>
      <router-link v-if="hasAdminRole"
        :to="{ name: 'mpi-logs', query: { f_transactionId: transaction.id } }">
        <v-btn small flat color="secondary">
          3DS Logs
          <v-icon small right dark class="ml-1">3d_rotation</v-icon>
        </v-btn>
      </router-link>
    </template>

    <v-layout :column="$vuetify.breakpoint.smAndDown">

      <v-flex class="mx-2">
        <m-pay-generic-viewer class="mb-2"
          :title="transaction.refundedTransactionId ? 'Refund Details' : 'Transaction Details'"
          :bean="transaction"
          :beanProperties="viewableProperties"
          :statusClasses="transactionStatusClasses"
          :formatters="tableFormatters"
          :roles="fieldRoles"
        >
        </m-pay-generic-viewer>
        <m-pay-generic-viewer class="mb-2"
                v-for="refund in transaction.refunds"
                :key="refund.id"
                title="Refund"
                :bean="refund"
                :beanProperties="['amount', 'date', 'transactionId', 'reasonCode', 'reasonNote']"
                :formatters="tableFormatters"
        >
        </m-pay-generic-viewer>
          <m-pay-generic-viewer
                  v-if="transaction.reversal && transaction.reversal.amount"
                  title="Reversal"
                  :bean="transaction.reversal"
                  :beanProperties="['amount', 'date', 'reasonCode', 'reasonNote']"
                  :formatters="tableFormatters"
          >
          </m-pay-generic-viewer>
          <m-pay-generic-viewer class="mb-2"
              v-if="transaction.reportData"
              title="Report details"
              :bean="transaction.reportData"
              :beanProperties="Object.keys(transaction.reportData)"
              :collapsed="true"
          >
          </m-pay-generic-viewer>
      </v-flex>


      <v-flex v-if="allowRefund || allowReversal || allowMarkAsSuccessful || allowMarkAsCancelled"
              class="mx-2">
        <v-card class="mb-4">
          <v-card-title>
            <span class="headline">Transaction Actions</span>
          </v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="allowReversal"
              color="error darken-1" flat large
              :loading="loading"
              :disabled="loading"
              @click.native="showReverseTransactionDialog = true">
              Reverse
              <v-icon right dark>compare_arrows</v-icon>
            </v-btn>
            <v-btn
              v-if="allowRefund"
              color="error darken-1" flat large
              :loading="loading"
              :disabled="loading"
              @click.native="showRefundTransactionDialog = true"
            >
              Refund
              <v-icon right dark>restore</v-icon>
            </v-btn>
            <v-btn
                v-if="allowRefund"
                color="error darken-1" flat large
                :loading="loading"
                :disabled="loading"
                @click.native="confirmRefundAlreadyDone"
            >
              Mark as refunded
              <v-icon right dark>restore</v-icon>
            </v-btn>
            <v-btn
                v-if="allowMarkAsSuccessful"
                color="error darken-1" flat large
                :loading="loading"
                :disabled="loading"
                @click.native="confirmMarkAsSuccessful"
            >
              Mark as successful
              <v-icon right dark>restore</v-icon>
            </v-btn>
            <v-btn
              v-if="allowMarkAsCancelled"
              color="error darken-1" flat large
              :loading="loading"
              :disabled="loading"
              @click.native="confirmMarkAsCancelled"
            >
              Release transaction
              <v-icon right dark>payment</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>

      <m-pay-transaction-reversal
        :show.sync="showReverseTransactionDialog"
        :transaction="transaction"
        @reverse="reverseTransaction"
      ></m-pay-transaction-reversal>

      <m-pay-transaction-refund
              :show.sync="showRefundTransactionDialog"
              :transaction="transaction"
              :manual-refund="false"
              @refund="refundTransaction"
      ></m-pay-transaction-refund>

      <m-pay-transaction-refund
          :show.sync="showManualRefundTransactionDialog"
          :manual-refund="true"
          :transaction="transaction"
          @refund="refundTransaction"
      ></m-pay-transaction-refund>

      <m-pay-confirm-dialog
          :show="showConfirmationDialog"
          :title="'Mark as refunded does NOT cause any actual money transfer. ' +
           'Has the refund already been done using another system?'"
          :cancel-text="'No'"
          @yes="showManualRefund"
          @cancel="hideDialog"
          maxWidth="700px"
      />

      <m-pay-confirm-dialog
          :show="showMarkAsSuccessfulDialog"
          :title="'Marking as successful does NOT cause any actual money transfer. ' +
           'Was the payment actually successful in another system?'"
          :cancel-text="'No'"
          @yes="markAsSuccessful"
          @cancel="hideMarkAsSuccessfulDialog"
          maxWidth="700px"
      />

      <m-pay-confirm-dialog
        :show="showMarkAsCancelledDialog"
        :title="`This action will release transaction, returning funds to the customer. Proceed?`"
        :cancel-text="'No'"
        @yes="markAsCancelled"
        @cancel="hideMarkAsCancelledDialog"
        maxWidth="700px"
      />

    </v-layout>
  </m-pay-layout>

</template>

<script>
import axios from 'axios';
import moment from 'moment';
import queries from '../utils/queries';
import roles from '../utils/user-roles';
import { transactionStatusClasses } from '../utils/statuses';
import formatters from '../utils/formatters';
import MPayGenericViewer from './MPayGenericViewer';
import MPayTransactionReversal from './MPayTransactionReversal';
import MPayTransactionRefund from './MPayTransactionRefund';
import MPayConfirmDialog from './MPayConfirmDialog';

export default {
  name: 'MPayTransactionDetailsView',

  components: {
    MPayGenericViewer,
    MPayTransactionReversal,
    MPayTransactionRefund,
    MPayConfirmDialog,
  },

  data: () => ({
    loading: false,
    transaction: {},
    viewableProperties: [
      'description',
      'amount',
      'currency',
      'date',
      'id',
      'status',
      'customerFirstName',
      'customerLastName',
      'purchaseId',
      'merchantName',
      'cardPan',
      'accountStatementReference',
      'externalErrorCode',
      'externalErrorDescription',
      'externalPaymentId',
      'batchId',
      'xid',
      'createdBy',
    ],
    fieldRoles: {
      customerFirstName: roles.READ_ANY_USER,
      customerLastName: roles.READ_ANY_USER,
      merchantName: roles.READ_ANY_USER,
    },
    transactionStatusClasses,
    formatters,
    tableFormatters: {
      amount: formatters.amount,
      date: formatters.dateTime,
      reversalDate: formatters.dateTime,
      reversalAmount: formatters.amount,
    },
    showReverseTransactionDialog: false,
    showRefundTransactionDialog: false,
    showManualRefundTransactionDialog: false,
    showConfirmationDialog: false,
    showMarkAsSuccessfulDialog: false,
    showMarkAsCancelledDialog: false,
  }),

  computed: {
    transactionAgeInDays() {
      const today = moment().startOf('day');
      const transactionDate = moment(this.transaction.date).startOf('day');
      return today.diff(transactionDate, 'days');
    },
    allowReversal() {
      return this.transaction.status === 'SUCCESS'
        && this.transactionAgeInDays < 360
        // BO-only role, no reversals in MC
        && this.$store.getters.hasAnyRole(roles.READ_ANY_TRANSACTION);
    },
    allowRefund() {
      return this.transaction.status === 'SUCCESS' && !this.transaction.refundedTransactionId;
    },
    allowMarkAsSuccessful() {
      return this.transaction.status !== 'SUCCESS' && this.transaction.status !== 'REVERSED';
    },
    allowMarkAsCancelled() {
      return this.transaction.status === 'RESERVATION' && this.transaction.reservationId;
    },
    hasAdminRole() {
      return this.$store.getters.hasAnyRole(roles.READ_ANY_USER);
    },
  },

  methods: {
    reverseTransaction(model) {
      this.loading = true;

      axios.put(queries.transactionAction(this.transaction.id, 'reverse'), model)
        .then((response) => {
          this.transaction = response.data;
          this.loading = false;
          this.showSuccessMessage('Transaction has been reversed successfully');
          this.fetchTransaction();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    markAsSuccessful() {
      this.loading = true;
      this.hideMarkAsSuccessfulDialog();

      axios.put(queries.transactionAction(this.transaction.id, 'successful'))
        .then((response) => {
          this.transaction = response.data;
          this.loading = false;
          this.showSuccessMessage('Transaction has been marked as successful');
          this.fetchTransaction();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    markAsCancelled() {
      this.loading = true;
      this.hideMarkAsCancelledDialog();

      axios.put(queries.transactionAction(this.transaction.id, 'cancel'))
        .then((response) => {
          this.transaction = response.data;
          this.loading = false;
          this.showSuccessMessage('Transaction has been released');
          this.fetchTransaction();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    refundTransaction(model) {
      this.loading = true;
      axios.put(queries.transactionAction(this.transaction.id, 'refund'), model)
        .then((response) => {
          this.transaction = response.data;
          this.loading = false;
          this.showSuccessMessage('Transaction has been refunded successfully');
          this.fetchTransaction();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    fetchTransaction() {
      this.loading = true;
      axios.get(this.$route.params.transactionId
        ? queries.transaction(this.$route.params.transactionId)
        : queries.transactionByQuery(this.$route.query))
        .then((response) => {
          this.transaction = response.data;
          this.loading = false;
        })
        .catch((e) => {
          this.transaction = {};
          this.showError(e);
          this.loading = false;
        });
    },
    confirmRefundAlreadyDone() {
      this.showConfirmationDialog = true;
    },
    showManualRefund() {
      this.showConfirmationDialog = false;
      this.showManualRefundTransactionDialog = true;
    },
    hideDialog() {
      this.showConfirmationDialog = false;
    },
    confirmMarkAsSuccessful() {
      this.showMarkAsSuccessfulDialog = true;
    },
    confirmMarkAsCancelled() {
      this.showMarkAsCancelledDialog = true;
    },
    hideMarkAsSuccessfulDialog() {
      this.showMarkAsSuccessfulDialog = false;
    },
    hideMarkAsCancelledDialog() {
      this.showMarkAsCancelledDialog = false;
    },
  },

  mounted() {
    this.fetchTransaction();
  },

};
</script>

<style>
.v-card__actions a {
  text-decoration: none;
}
</style>



// WEBPACK FOOTER //
// src/components/MPayTransactionDetailsView.vue